import React, { useCallback, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Image, Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import clsx from 'clsx';
import { ActionButton, PrimaryButton } from 'common/components/Buttons/v8';
import { Card } from 'common/components/Cards/Card';
import { Link } from 'common/components/Link';
import { Heading1, Text } from 'common/components/Text';
import { useScreen } from 'common/hooks/useScreen';
import { ImageConstants } from 'common/ImageConstants';
import { getAssetPath } from 'common/utilities/AssetsPath';
import { pageDataService, UnsubscribeStatus } from 'common/utilities/PageDataService';
import { useLogViewOnMount } from 'common/utilities/telemetryHelpers';
import { Configuration } from 'Configuration';
import DigestFeedbackPage from 'features/feedback/DigestFeedbackPage';
import { useLocalizedStrings } from 'i18n';
import { dashboardPath } from 'Routes';
import { useAssetPath } from 'store/hooks';
import { useUnauthNotificationSettings } from 'store/notifications/useUnauthNotificationSettings';

import { stackTokens, useStyles } from './UnsubscribePage.styles';

type SubscriptionContextType = 'Unsubscribed' | 'AlreadyUnsubscribed' | 'Resubscribed';

const notificationSettingsBaseUrl = '/signin?sidePanel_open=NotificationSettings&sidePanel_settingType=Email';

const footerUrls = {
  legal: 'https://go.microsoft.com/fwlink/?linkid=866263', // serviceagreement
  privacyStatement: 'https://go.microsoft.com/fwlink/?LinkId=521839', // privacy/privacystatement
  consumerHealthPrivacy: 'https://go.microsoft.com/fwlink/?linkid=2259814', // privacy/microsoft-consumer-health-data-privacy-policy
};

const telemetryPrefix = 'UnsubscribePage';

const UnsubscribePage = () => {
  const { isTokenExpired, userId, email, status, notificationFeature } = pageDataService.getUnsubscribeData() || {};
  const { resubscribe, isResubscribed } = useUnauthNotificationSettings();

  const { isMobileScreen } = useScreen();
  const styles = useStyles();
  const { t } = useLocalizedStrings();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFeedbackDialogOpen, { setTrue: openFeedbackDialog, setFalse: closeFeedbackDialog }] = useBoolean(false);

  const telemetryProps = { userId, notificationFeature, status, isTokenExpired };

  // Note: PNG version of images have been used for all image variants, these will be converted to SVGs once all images are ready in SVG format.
  const defenderLogo = getAssetPath(ImageConstants.DefenderShield);
  const emailDismiss = useAssetPath(ImageConstants.EmailDismissPng);
  const emailShield = useAssetPath(ImageConstants.EmailShieldPng);

  useLogViewOnMount(telemetryPrefix, telemetryProps);

  const subscriptionContext: SubscriptionContextType = useMemo(() => {
    if (isResubscribed) {
      return 'Resubscribed';
    }
    return status === UnsubscribeStatus.AlreadyUnsubscribed ? 'AlreadyUnsubscribed' : 'Unsubscribed';
  }, [isResubscribed, status]);

  const title = useMemo(() => {
    // i18next-extract-mark-context-start ["Unsubscribed", "AlreadyUnsubscribed", "Resubscribed"]
    return t('UnsubscribePage.Title.MonthlyDigest', { context: subscriptionContext });
    // i18next-extract-mark-context-stop
  }, [subscriptionContext, t]);

  const manageNotificationsUrl = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete('token');
    if (queryParams.size > 0) {
      return `${notificationSettingsBaseUrl}&${queryParams.toString()}`;
    }
    return notificationSettingsBaseUrl;
  }, []);

  const subTitle = useMemo(() => {
    return (
      // i18next-extract-disable-next-line
      <Trans
        t={t}
        i18nKey={`UnsubscribePage.Subtitle.MonthlyDigest_${subscriptionContext}`}
        values={{ email, manageLabel: t('UnsubscribePage.ManageNotificationsLink') }}
        components={{
          b: <b />,
          manageLink: <Link href={manageNotificationsUrl} telemetryEventName={`${telemetryPrefix}_ManageYourNotifications`} />,
        }}
      />
    );
  }, [email, subscriptionContext, manageNotificationsUrl, t]);

  const onResubscribeClick = useCallback(() => {
    setIsLoading(true);
    resubscribe().finally(() => {
      // set loading to false after the API call is completed
      setIsLoading(false);
    });
  }, [resubscribe, setIsLoading]);

  const footerLinks = useMemo((): { id: string; label: string; url: string }[] => {
    return [
      {
        id: 'Legal',
        label: t('UnsubscribePage.Footer.Legal'),
        url: footerUrls.legal,
      },
      {
        id: 'PrivacyStatement',
        label: t('UnsubscribePage.Footer.PrivacyStatement'),
        url: footerUrls.privacyStatement,
      },
      {
        id: 'ConsumerHealthPrivacy',
        label: t('UnsubscribePage.Footer.ConsumerHealthPrivacy'),
        url: footerUrls.consumerHealthPrivacy,
      },
    ];
  }, [t]);

  const renderFooter = useCallback(
    () => (
      <Stack horizontal horizontalAlign="center" verticalAlign="center" tokens={stackTokens.footer}>
        {footerLinks.map((link) => (
          <Stack.Item key={link.id}>
            <Text variant="Caption2">
              <Link href={link.url} target="_blank" telemetryEventName={`${telemetryPrefix}_Footer_${link.id}`}>
                {link.label}
              </Link>
            </Text>
          </Stack.Item>
        ))}
      </Stack>
    ),
    [footerLinks]
  );

  return (
    <Stack
      verticalAlign="space-between"
      verticalFill
      horizontalAlign="center"
      tokens={stackTokens.pageWrapper}
      className={styles.pageWrapper}
    >
      <Card className={styles.card} maxWidth={526}>
        <Stack tokens={stackTokens.cardContentWrapper}>
          <Stack horizontal verticalAlign="center" tokens={stackTokens.logoHeading} className={styles.logoHeading}>
            <Stack.Item>
              <Image alt="" width={24} src={defenderLogo} />
            </Stack.Item>
            <Stack.Item className={styles.productNameWrapper}>
              <Text variant="Body2">{Configuration.FullProductName}</Text>
            </Stack.Item>
          </Stack>

          <Stack tokens={stackTokens.cardContent}>
            <Stack.Item align="center">
              <Image alt="" width={192} src={isResubscribed ? emailShield : emailDismiss} />
            </Stack.Item>
            <Stack.Item className={styles.text}>
              <Heading1 variant={isMobileScreen ? 'Title2Strong' : 'Title3'}>{title}</Heading1>
            </Stack.Item>
            <Stack.Item className={styles.text}>
              <Text variant="Body1">{subTitle}</Text>
            </Stack.Item>
          </Stack>

          <Stack verticalAlign="center" horizontalAlign="center" tokens={stackTokens.buttonSection} className={styles.buttonSection}>
            <Stack.Item>
              {isResubscribed ? (
                <PrimaryButton
                  href={dashboardPath}
                  telemetryEventName={`${telemetryPrefix}_OpenDefender`}
                  className={clsx(styles.button, styles.flexButton)}
                >
                  {t('UnsubscribePage.PrimaryButton.OpenDefender')}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={onResubscribeClick}
                  telemetryEventName={`${telemetryPrefix}_Resubscribe`}
                  isLoading={isLoading}
                  className={styles.button}
                >
                  {t('UnsubscribePage.PrimaryButton.Resubscribe')}
                </PrimaryButton>
              )}
            </Stack.Item>
            {!isResubscribed && (
              <Stack.Item>
                <ActionButton onClick={openFeedbackDialog} telemetryEventName={`${telemetryPrefix}_TellUsHowWeCanImprove`}>
                  {t('UnsubscribePage.FeedbackLink')}
                </ActionButton>
              </Stack.Item>
            )}
          </Stack>
        </Stack>
      </Card>
      {isFeedbackDialogOpen && <DigestFeedbackPage disableRating={true} onDismiss={closeFeedbackDialog} />}
      {renderFooter()}
    </Stack>
  );
};

export default UnsubscribePage;
